'use client';
import React from 'react';
import classNames from 'classnames';
import styled from '@benzinga/themetron';
// import { isDesktop, isMobile } from '@benzinga/device-utils';
import { elementIsVisibleInViewport } from '@benzinga/frontend-utils';
import type { Sizes, SizeMap } from '@benzinga/ads';

const GoogleAdUnit = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.GoogleAdUnit };
  }),
);

export interface GoogleAdBlockProps {
  attrs: {
    data: {
      className?: string;
      slot_placement_id: string;
      slot_width: string;
      slot_height: string;
      slot_google_ad_id?: string;
      dynamic_height?: boolean;
      display?: 'all-devices' | 'mobile' | 'desktop';
      size_mapping?: SizeMap[];
    };
  };
}

const getNetworkId = (parts: string[]): string | null => {
  if (parts?.length === 1) {
    return '4107070';
  } else if (parts?.length === 2) {
    return parts[0];
  } else if (parts?.length === 3) {
    return parts[1];
  }
  return null;
};

const getAdUnitId = (parts: string[]): string | null => {
  if (parts?.length === 1) {
    return parts[0];
  } else if (parts?.length === 2) {
    return parts[1];
  } else if (parts?.length === 3) {
    return parts[2];
  }
  return null;
};

export const GoogleAdBlock: React.FC<GoogleAdBlockProps> = React.memo(({ attrs }) => {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (wrapperRef.current) {
      if (!elementIsVisibleInViewport(wrapperRef.current, true)) {
        wrapperRef.current.style.minHeight = '0';
      }
    }
  }, [wrapperRef]);

  if (!attrs?.data) return <div />;
  const { display, dynamic_height, size_mapping, slot_google_ad_id, slot_height, slot_placement_id, slot_width } =
    attrs.data;
  const parts = slot_placement_id.split('/');
  const dfpNetworkId = getNetworkId(parts);
  const adUnit = getAdUnitId(parts);
  if (dfpNetworkId && adUnit) {
    const height = parseInt(slot_height);
    const width = parseInt(slot_width);
    const sizes: Sizes = [[width, height]];
    if (size_mapping) {
      size_mapping.forEach((map: SizeMap) => {
        map.sizes.forEach(size => {
          sizes.push(size);
        });
      });
    }

    const wrapperClassName = classNames(
      'google-ad-placement',
      attrs.data.className,
      display ? (display === 'all-devices' ? 'all-devices' : `${display}-only`) : undefined,
    );

    return (
      <GoogleAdWrapper className={wrapperClassName}>
        <div
          className="ad-container"
          ref={wrapperRef}
          style={
            dynamic_height
              ? { minHeight: typeof height === 'number' ? `${height}px` : 'unset' }
              : { height: typeof height === 'number' ? `${height}px` : 'unset' }
          }
        >
          <React.Suspense fallback={<div />}>
            <GoogleAdUnit
              adUnit={adUnit}
              dfpNetworkId={dfpNetworkId}
              sizeMapping={size_mapping}
              sizes={sizes}
              slotId={slot_google_ad_id}
            />
            {/* {(isMobile() && display === 'desktop') || (isDesktop() && display === 'mobile') ? null : (
            )} */}
          </React.Suspense>
        </div>
      </GoogleAdWrapper>
    );
  } else {
    return <div />;
  }
});

export const GoogleAdWrapper = styled.div`
  .ad-container {
    margin: 16px auto;
    align-items: center;
    display: flex;
  }
  &.desktop-only {
    display: block;
  }
  &.mobile-only {
    display: none;
  }
  @media (max-width: 800px) {
    .ad-container {
      max-width: 300px;
      overflow: scroll;
    }
    &.desktop-only {
      display: none;
    }
    &.mobile-only {
      display: block;
    }
  }
`;
